import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthDialog from "@/components/AuthDialog";
import TestModeBanner from "@/components/TestModeBanner";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { CountryFlag } from "@/lib/identity";
import {
  ArrowRight, Broadcast, Sword, Fire, Stairs, ShieldStar, Crown,
} from "@phosphor-icons/react";

/**
 * Landing — the public marketing page.
 *
 * Deliberately trimmed to 5 sections + footer. No decorative kente stripes
 * between every section, no corner ornaments, no double titles.
 * Copy is written to sound like a real person, not marketing.
 */
const MODES = [
  { Icon: Broadcast, name: "NK Daily",  line: "A live show, three times a day. Answer along, beat the room." },
  { Icon: Stairs,    name: "NK Ladder", line: "15 rungs. Cash out early or push for the summit." },
  { Icon: Sword,     name: "NK Duel",   line: "1-v-1. Your rating goes up when you win, down when you don't." },
  { Icon: Fire,      name: "NK Escape", line: "Nine rooms. One wrong answer and you're out. Simple." },
];

const FAQ = [
  {
    q: "How much does it cost?",
    a: "Nothing. Playing is free. NK+ is a small monthly upgrade if you want extra lifelines and no ads.",
  },
  {
    q: "Do I have to be in Ghana or Nigeria?",
    a: "No. Anyone from any country can play. Cash prizes are live in Ghana and Nigeria first — everywhere else, you still compete for your national leaderboard and titles.",
  },
  {
    q: "How long is a season?",
    a: "30 days. On day 31, we crown champions. Then we start over.",
  },
  {
    q: "What do I need to sign up?",
    a: "An email address or a Google account. Pick your country and you're in.",
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [war, setWar] = useState(null);

  useEffect(() => {
    if (!loading && user && user.country && user.username) {
      navigate("/home", { replace: true });
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    api.get("/season/country-war").then((r) => setWar(r.data)).catch(() => {});
  }, []);

  const openAuth = () => setAuthOpen(true);

  const totalPlayers = (war?.rows || []).reduce((a, b) => a + (b.player_count || 0), 0);
  const topCountries = (war?.rows || []).slice(0, 5);

  return (
    <div className="min-h-screen bg-nk-bg text-nk-text">
      <TestModeBanner />

      {/* Nav */}
      <header className="border-b border-nk-line/50 bg-nk-bg/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 shrink-0">
            <div
              className="w-7 h-7 rounded-sm flex items-center justify-center"
              style={{ background: "#0B3D2E", border: "1px solid #D4AF37" }}
            >
              <span className="font-display font-bold text-nk-gold text-sm">N</span>
            </div>
            <span className="font-display text-lg font-medium tracking-tight">NKognito</span>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <button
              onClick={openAuth}
              data-testid="landing-signin-link"
              className="text-sm text-nk-mute hover:text-nk-text transition-colors"
            >
              Sign in
            </button>
            <button
              data-testid="hero-cta-join"
              onClick={openAuth}
              className="inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-sm bg-nk-gold text-nk-charcoal hover:bg-nk-gold/85 transition-colors"
            >
              Play free
            </button>
          </div>
        </div>
      </header>

      {/* 1 · HERO */}
      <section className="border-b border-nk-line/40" data-testid="landing-hero">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 pt-20 pb-16 sm:pt-28 sm:pb-24">
          <p className="eyebrow mb-4 text-nk-gold/80">Season 2 · open now</p>
          <h1 className="font-display text-4xl sm:text-6xl font-medium tracking-tight leading-[1.05] max-w-3xl">
            Play trivia.<br />
            Represent your country.<br />
            <span className="text-nk-gold">Win the month.</span>
          </h1>
          <p className="text-base sm:text-lg text-nk-mute mt-6 max-w-xl leading-relaxed">
            NKognito is a 30-day knowledge league. Every game you play moves you up your country's leaderboard.
            On day 31, we crown champions.
          </p>
          <div className="mt-8 flex items-center gap-4 flex-wrap">
            <button
              onClick={openAuth}
              data-testid="hero-primary-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-sm font-medium bg-nk-gold text-nk-charcoal hover:bg-nk-gold/85 transition-colors"
            >
              Play now <ArrowRight size={14} weight="bold" />
            </button>
            <a
              href="#how"
              className="text-sm text-nk-mute hover:text-nk-text transition-colors"
            >
              See how it works →
            </a>
          </div>
          <p className="text-xs text-nk-dim mt-4">Free forever. No wallet, no deposit.</p>
        </div>
      </section>

      {/* 2 · MODES */}
      <section id="how" className="border-b border-nk-line/40" data-testid="landing-modes">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 py-16 sm:py-20">
          <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight mb-2">
            Four ways to play.
          </h2>
          <p className="text-sm text-nk-mute mb-8 max-w-lg">
            Pick a mode. Play a round. Come back tomorrow, or don't — the league doesn't care.
            Every point you earn stays with your country.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {MODES.map((m) => (
              <article
                key={m.name}
                className="nk-card p-5"
                data-testid={`landing-mode-${m.name.toLowerCase().replace(/\s/g,"-")}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <m.Icon size={18} weight="duotone" className="text-nk-gold" />
                  <h3 className="font-display text-lg font-medium">{m.name}</h3>
                </div>
                <p className="text-sm text-nk-mute leading-relaxed">{m.line}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 3 · THE SEASON — how the month works */}
      <section className="border-b border-nk-line/40" data-testid="landing-season">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 py-16 sm:py-20">
          <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight mb-8">
            One season. Thirty days.
          </h2>
          <ol className="space-y-6 max-w-2xl">
            <SeasonStep
              n="1"
              title="Sign up. Pick your country."
              body="Free. Email or Google. Once you set your country, that's your team for the season."
            />
            <SeasonStep
              n="2"
              title="Play whenever."
              body="Live shows three times a day, plus solo modes any time. Every game adds to your Season Score."
            />
            <SeasonStep
              n="3"
              title="Day 31: champions crowned."
              body="Top players from Ghana and Nigeria play a national final for cash. Everyone else gets titles, badges, and a fresh leaderboard reset."
            />
          </ol>
        </div>
      </section>

      {/* 4 · NATIONS SNAPSHOT (only if there are numbers to show) */}
      {totalPlayers > 0 && (
        <section className="border-b border-nk-line/40" data-testid="landing-nations">
          <div className="max-w-5xl mx-auto px-5 sm:px-6 py-16 sm:py-20">
            <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight mb-2">
              Nations at play.
            </h2>
            <p className="text-sm text-nk-mute mb-6 max-w-lg">
              {totalPlayers.toLocaleString()} players competing right now. Your country's total = every player's Season Score, added up. Move your flag.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl">
              {topCountries.map((r, i) => (
                <div
                  key={r.country}
                  className="flex items-center justify-between px-4 py-3 border border-nk-line/50 rounded-sm bg-nk-surface/20"
                  data-testid={`landing-nation-${r.country}`}
                >
                  <span className="flex items-center gap-3">
                    <span className="text-xs text-nk-mute font-mono tabular-nums w-4">#{i + 1}</span>
                    <CountryFlag country={r.country} size={14} />
                    <span className="text-sm">{r.country_name || r.country}</span>
                  </span>
                  <span className="font-mono text-sm text-nk-gold tabular-nums">
                    {(r.total_score || 0).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 5 · FAQ */}
      <section id="faq" className="border-b border-nk-line/40" data-testid="landing-faq">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 py-16 sm:py-20">
          <h2 className="font-display text-2xl sm:text-3xl font-medium tracking-tight mb-8">
            Common questions.
          </h2>
          <dl className="space-y-6 max-w-2xl">
            {FAQ.map((qa) => (
              <div key={qa.q} data-testid={`landing-faq-${qa.q.substring(0, 20).toLowerCase().replace(/\s/g,"-")}`}>
                <dt className="font-medium text-nk-text mb-1.5">{qa.q}</dt>
                <dd className="text-sm text-nk-mute leading-relaxed">{qa.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Final CTA */}
      <section data-testid="landing-final-cta">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-20 sm:py-24 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight mb-3">
            The season is already running.
          </h2>
          <p className="text-sm text-nk-mute mb-8 max-w-md mx-auto">
            You've got 30 days to prove your country. Start with a single round.
          </p>
          <button
            onClick={openAuth}
            data-testid="final-cta-join"
            className="inline-flex items-center gap-2 px-7 py-4 rounded-sm font-medium text-base bg-nk-gold text-nk-charcoal hover:bg-nk-gold/85 transition-colors"
          >
            Play free <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-nk-line/50 bg-nk-surface/20">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 py-10 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5 text-xs text-nk-mute">
            <div
              className="w-6 h-6 rounded-sm flex items-center justify-center"
              style={{ background: "#0B3D2E", border: "1px solid #D4AF37" }}
            >
              <span className="font-display font-bold text-nk-gold text-[10px]">N</span>
            </div>
            <span>© {new Date().getFullYear()} NKognito. Africa&apos;s knowledge league.</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-nk-mute">
            <Link to="/partner" className="hover:text-nk-gold transition-colors">Partner</Link>
            <Link to="/admin/login" data-testid="landing-admin-link" className="hover:text-nk-gold transition-colors inline-flex items-center gap-1">
              <ShieldStar size={11} weight="duotone" /> Admin
            </Link>
            <Link to="/host/login" data-testid="landing-host-link" className="hover:text-nk-gold transition-colors inline-flex items-center gap-1">
              <Crown size={11} weight="duotone" /> Host
            </Link>
          </div>
        </div>
      </footer>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}

function SeasonStep({ n, title, body }) {
  return (
    <li className="flex gap-4" data-testid={`landing-season-step-${n}`}>
      <span className="font-display text-2xl text-nk-gold/70 tabular-nums leading-none shrink-0 pt-0.5">
        {n}
      </span>
      <div>
        <h3 className="font-display text-base sm:text-lg font-medium mb-1">{title}</h3>
        <p className="text-sm text-nk-mute leading-relaxed">{body}</p>
      </div>
    </li>
  );
}
